import React from 'react';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { services } from '../mock/mockData';
import usePageTitle from '../hooks/usePageTitle';
import * as LucideIcons from 'lucide-react';

const Services = () => {
  usePageTitle('Our Services');
  const detailedServices = [
    {
      title: 'Bulk Steel Supply',
      description: 'We specialize in supplying large volumes of steel products for major construction projects, infrastructure development, and industrial applications. Our extensive inventory and reliable supply chain ensure uninterrupted material availability for your projects.',
      features: [
        'Large volume orders (100+ tonnes)',
        'Consistent quality across batches',
        'Flexible delivery schedules',
        'Dedicated account manager',
        'Priority order processing'
      ]
    },
    {
      title: 'Custom Cutting & Fabrication',
      description: 'Our state-of-the-art fabrication facility offers precision cutting, bending, and fabrication services to meet your exact specifications. Save time and reduce waste with our custom steel solutions.',
      features: [
        'CNC cutting services',
        'Bending and forming',
        'Welding and assembly',
        'Custom dimensions',
        'CAD/CAM support'
      ]
    },
    {
      title: 'Quality Testing & Certification',
      description: 'Quality is our top priority. Every batch of steel undergoes rigorous testing to ensure it meets industry standards and your specific requirements.',
      features: [
        'Mill test certificates (MTC)',
        'Third-party testing',
        'Chemical composition analysis',
        'Mechanical properties testing',
        'ISO 9001:2015 certified processes'
      ]
    },
    {
      title: 'Logistics & Delivery',
      description: 'Our robust logistics network covers all major cities and industrial areas across India. We ensure your steel products reach your site on time, every time.',
      features: [
        'Pan-India delivery network',
        'Real-time shipment tracking',
        'Door-to-door delivery',
        'Safe and secure packaging',
        'Flexible delivery timings'
      ]
    },
    {
      title: 'Technical Consultation',
      description: 'Our team of experienced engineers and steel specialists provide expert guidance on material selection, specifications, and application suitability.',
      features: [
        'Free technical consultation',
        'Material grade recommendations',
        'Cost optimization suggestions',
        'Structural design support',
        'Industry best practices'
      ]
    },
    {
      title: 'Inventory Management',
      description: 'For our long-term clients, we offer inventory management services to help optimize your steel procurement and reduce storage costs.',
      features: [
        'Just-in-time delivery',
        'Vendor-managed inventory',
        'Stock level monitoring',
        'Automated reordering',
        'Usage analytics'
      ]
    }
  ];

  const industries = [
    {
      name: 'Construction & Infrastructure',
      description: 'TMT bars, structural steel, and reinforcement materials for buildings, bridges, and highways',
      icon: 'Building2'
    },
    {
      name: 'Manufacturing',
      description: 'Steel sheets, coils, and plates for automotive, machinery, and equipment manufacturing',
      icon: 'Factory'
    },
    {
      name: 'Oil & Gas',
      description: 'Seamless pipes, flanges, and fittings for petrochemical and energy sector applications',
      icon: 'Fuel'
    },
    {
      name: 'Power & Energy',
      description: 'Structural steel and specialized materials for power plants and renewable energy projects',
      icon: 'Zap'
    },
    {
      name: 'Railways',
      description: 'Rails, sleepers, and structural components for railway infrastructure projects',
      icon: 'Train'
    },
    {
      name: 'Shipbuilding',
      description: 'Marine-grade steel plates and sections for shipyards and maritime applications',
      icon: 'Ship'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Our <span className="text-orange-600">Services</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Comprehensive steel solutions from supply to delivery, backed by 25+ years of industry expertise
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card key={service.id} className="border-slate-200 hover:shadow-lg transition-all duration-300 bg-white group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-orange-600 transition-colors">
                      <IconComponent className="w-7 h-7 text-orange-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Service Details</h2>
              <p className="text-xl text-slate-600">
                Learn more about what we offer
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {detailedServices.map((service, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-2 border-slate-200 rounded-lg px-6 hover:border-orange-600 transition-colors"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="text-xl font-bold text-slate-900">{service.title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-slate-700 mb-6 leading-relaxed">{service.description}</p>
                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Trusted steel partner across diverse sectors and applications
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => {
              const IconComponent = LucideIcons[industry.icon];
              return (
                <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300 bg-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                      <IconComponent className="w-7 h-7 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{industry.name}</h3>
                    <p className="text-slate-600 leading-relaxed">{industry.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Process</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Simple and efficient workflow from inquiry to delivery
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Inquiry', description: 'Share your requirements' },
                { step: '02', title: 'Quotation', description: 'Receive competitive quote' },
                { step: '03', title: 'Order', description: 'Confirm your order' },
                { step: '04', title: 'Delivery', description: 'On-time delivery' }
              ].map((item, index) => (
                <div key={index} className="text-center relative">
                  <div className="w-20 h-20 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                  {index < 3 && (
                    <ArrowRight className="hidden md:block absolute top-10 -right-6 w-8 h-8 text-orange-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your steel requirements and get a competitive quote
          </p>
          <Link to="/contact">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg">
              Request a Quote <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
